import type { Ring, Vec2 } from '../../../../geo/types';

/**
 * The east end of the Nieuwe Kerk as the floor plan draws it (Reliwiki plattegrond, registered to the
 * trace; docs/plans/landmarks.md 5.5): the high choir closed in five sides of a dodecagon, its straight
 * walls turning thirty degrees at every corner, the ambulatory round it and the radiating chapels on
 * its outer sides, each walled out to the buttresses it shares with its neighbours.
 */
export const CHEVET = { apseSides: 12, facets: 5 } as const;

const TURN = (2 * Math.PI) / CHEVET.apseSides;

/**
 * The apse from the chord's south end round the east to its north end: five equal sides tangent to
 * the circle of the vessel's half width, so every side stands as far from the ridge's end as the long
 * walls do.
 */
export function apseRing(chordU: number, vc: number, half: number): Ring {
  const side = 2 * half * Math.tan(TURN / 2);
  const points: Vec2[] = [[chordU, vc - half]];
  for (let k = 1; k < CHEVET.facets; k++) {
    const [u, v] = points[k - 1]!;
    points.push([u + side * Math.cos(k * TURN), v + side * Math.sin(k * TURN)]);
  }
  return [...points, [chordU, vc + half]];
}

/** Where the ridge ends over the apse: the centre of the circle its sides stand on. */
export function apseHipU(chordU: number, half: number): number {
  return chordU - half * Math.tan(TURN / 2);
}

function rightNormal(a: Vec2, b: Vec2): Vec2 {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  return [(b[1] - a[1]) / len, -(b[0] - a[0]) / len];
}

/**
 * An open polyline moved `distance` to the right of its direction of travel, its corners mitred:
 * the outside of a line walked counter-clockwise round a building.
 */
export function offsetRight(line: readonly Vec2[], distance: number): Vec2[] {
  const normals = line.slice(1).map((p, i) => rightNormal(line[i]!, p));
  return line.map((p, i): Vec2 => {
    const before = normals[Math.max(0, i - 1)]!;
    const after = normals[Math.min(normals.length - 1, i)]!;
    const scale = distance / (1 + before[0] * after[0] + before[1] * after[1]);
    return [p[0] + (before[0] + after[0]) * scale, p[1] + (before[1] + after[1]) * scale];
  });
}

/** The mean of a ring's corners. */
export function ringCentre(ring: Ring): Vec2 {
  const [u, v] = ring.reduce((s, p): Vec2 => [s[0] + p[0], s[1] + p[1]], [0, 0]);
  return [u / ring.length, v / ring.length];
}

/** The least distance from a point to the sides of a ring. */
export function insetOf(ring: Ring, p: Vec2): number {
  return Math.min(
    ...ring.map((a, i) => {
      const b = ring[(i + 1) % ring.length]!;
      const [du, dv] = [b[0] - a[0], b[1] - a[1]];
      const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * du + (p[1] - a[1]) * dv) / (du * du + dv * dv)));
      return Math.hypot(p[0] - a[0] - t * du, p[1] - a[1] - t * dv);
    })
  );
}
